import ExportedImage from 'next-image-export-optimizer';
import React, { useEffect, useState, type ComponentProps } from 'react';
import footerBannerImageSrc from '../../../../public/img/banner-2025-new-students.webp';
import ListIcon from '../../../../public/img/list-ul-solid-icon.svg';
import { PageUrl } from '../../../constants/PageUrl';
import { FooterBanner } from '../../class_landing_page/molecules/FooterBanner';
import Footer from '../../common/Footer';
import { Header } from '../../common/Header';
import { PromotionAlert } from '../../common/PromotionAlert';
import { Breadcrumb } from '../Breadcrumb';
import styles from './index.module.scss';

type Props = {
  children: React.ReactNode;
  thumbnail: string;
  thumbnailAlt?: string;
  title: string;
  promotion: boolean;
};

type Heading = {
  level: number;
  value: string;
  slug: string;
};

// 見出しの children は常に string とは限らない（React.ReactNode 一般。数値・配列・ネストした要素になりうる）ため、再帰的にテキストへ変換する
const getNodeText = (node: React.ReactNode): string => {
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join('');
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return getNodeText(node.props.children);
  }
  return '';
};

const Base = ({ children, thumbnail, thumbnailAlt, title, promotion }: Props) => {
  const [headings, setHeadings] = useState<Heading[]>([]);

  const createSlug = (value: string) => value.toLowerCase().replace(/\s+/g, '-');

  // 同一記事内に同名見出し（h2/h3/h4）が複数ある場合、ID重複でフラグメント遷移が壊れないよう連番を振る
  const headingSlugs = React.useMemo(() => {
    const counts = new Map<string, number>();
    const slugs: (string | undefined)[] = [];

    React.Children.forEach(children, (child) => {
      if (
        React.isValidElement<{ children?: React.ReactNode }>(child) &&
        (child.type === 'h2' || child.type === 'h3' || child.type === 'h4')
      ) {
        const value = getNodeText(child.props.children);
        const baseSlug = createSlug(value);
        const count = counts.get(baseSlug) ?? 0;
        counts.set(baseSlug, count + 1);
        slugs.push(count === 0 ? baseSlug : `${baseSlug}-${count + 1}`);
      } else {
        slugs.push(undefined);
      }
    });

    return slugs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  useEffect(() => {
    const newHeadings: Heading[] = [];

    // 除外するキーワードを定義
    const excludeKeywords = ['目次'];

    React.Children.forEach(children, (child, index) => {
      if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
        const value = getNodeText(child.props.children);
        const slug = headingSlugs[index] ?? createSlug(value);
        if (child.type === 'h2') {
          newHeadings.push({ level: 2, value, slug });
        } else if (child.type === 'h3') {
          newHeadings.push({ level: 3, value, slug });
        }
      }
    });
    const filteredHeadings = newHeadings.filter((item) => !excludeKeywords.includes(item.value));

    setHeadings(filteredHeadings);
  }, [children, headingSlugs]);

  const menuList: ComponentProps<typeof Header>['menuList'] = [
    {
      text: '保育園',
      href: PageUrl.DAYCARE_TOP,
    },
    {
      text: 'ブログトップ',
      href: PageUrl.BLOG_ROOT,
    },
    { text: 'モンテチャット', href: PageUrl.MONTE_CHAT_ROOT },
  ];

  return (
    <>
      <div className={styles.base}>
        <Header menuList={menuList} buttonList={[]} />
        <div className={styles.body}>
          <div className={styles['body-inner']}>
            <Breadcrumb title={title} />
            <ExportedImage
              alt={thumbnailAlt ?? title}
              src={thumbnail}
              className={styles.thumbnail}
              width={400}
              height={300}
              loading="lazy"
            />
            <h1>{title}</h1>
            {promotion ? <PromotionAlert /> : null}

            {React.Children.map(children, (child, index) => {
              if (!React.isValidElement(child)) {
                return child;
              }

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (child.props.children === '目次') {
                return <TableOfContents headings={headings} />;
              }
              if (
                (child.type === 'h2' || child.type === 'h3' || child.type === 'h4') &&
                React.isValidElement<{ children?: React.ReactNode }>(child)
              ) {
                const id = headingSlugs[index] ?? createSlug(getNodeText(child.props.children));
                return <child.type id={id}>{child.props.children}</child.type>;
              }

              // Enhanced table detection logic
              const isTableElement = child.type === 'table';
              const isMdxTable = child.props?.mdxType === 'table';
              const hasTableClass = child.props?.className?.includes?.('table');

              // Check for markdown table pattern in paragraph content
              const childrenContent = child.props?.children;
              const contentString =
                typeof childrenContent === 'string'
                  ? childrenContent
                  : Array.isArray(childrenContent)
                  ? childrenContent.map(reactNodeToHtmlString).join('')
                  : '';
              const isMarkdownTable =
                child.type === 'p' && contentString.includes('|') && contentString.includes('---');

              if (isTableElement || isMdxTable || hasTableClass) {
                if (process.env.NODE_ENV === 'development') {
                  /* eslint-disable no-console */
                  console.log('🎯 HTML TABLE DETECTED - Wrapping with table-container');
                  console.log('Detection method:', {
                    isTableElement,
                    isMdxTable,
                    hasTableClass,
                  });
                  /* eslint-enable no-console */
                }
                const tableWithDataLabels = addDataLabelsToTable(child);
                return <div className={styles['table-container']}>{tableWithDataLabels}</div>;
              }

              // Handle markdown tables that weren't parsed into HTML
              if (isMarkdownTable) {
                if (process.env.NODE_ENV === 'development') {
                  /* eslint-disable no-console */
                  console.log('🎯 MARKDOWN TABLE DETECTED - Converting to HTML table');
                  console.log('Table content:', `${contentString.substring(0, 200)}...`);
                  /* eslint-enable no-console */
                }
                return <MarkdownTableConverter content={contentString} />;
              }

              return child;
            })}
          </div>
        </div>
      </div>
      <FooterBanner imageSrc={footerBannerImageSrc} href={PageUrl.DAYCARE_TOP} />
      <Footer maxWidth="700px" variant="mobile" />
    </>
  );
};

// Helper to serialize a React node (string or element) to an HTML string
const reactNodeToHtmlString = (node: unknown): string => {
  if (typeof node === 'string') return node;
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    const tag = typeof node.type === 'string' ? node.type : '';
    const inner = Array.isArray(node.props.children)
      ? node.props.children.map(reactNodeToHtmlString).join('')
      : typeof node.props.children === 'string'
      ? node.props.children
      : '';
    return tag ? `<${tag}>${inner}</${tag}>` : inner;
  }
  return '';
};

type TableNodeProps = {
  children?: React.ReactNode;
  mdxType?: string;
  'data-label'?: string;
  role?: React.AriaRole;
  scope?: string;
};

const isTableTag = (node: React.ReactNode, tag: string): node is React.ReactElement<TableNodeProps> =>
  React.isValidElement<TableNodeProps>(node) && (node.type === tag || node.props.mdxType === tag);

// Clone a <table> element's <tbody> <td> cells with a data-label attribute derived from the
// matching <thead> column, so mobile CSS can render each row as a labelled card/stack instead
// of a horizontally-scrolling table.
const addDataLabelsToTable = (table: React.ReactElement): React.ReactElement => {
  if (!isTableTag(table, 'table')) return table;

  const sections = React.Children.toArray(table.props.children);
  const thead = sections.find((section) => isTableTag(section, 'thead'));
  const headerRow = isTableTag(thead, 'thead') ? React.Children.toArray(thead.props.children)[0] : undefined;
  const headers = isTableTag(headerRow, 'tr')
    ? React.Children.toArray(headerRow.props.children).map((cell) =>
        isTableTag(cell, 'th') ? getNodeText(cell.props.children) : ''
      )
    : [];

  const newChildren = sections.map((section) => {
    if (!isTableTag(section, 'thead') && !isTableTag(section, 'tbody')) return section;

    const newRows = React.Children.map(section.props.children, (row) => {
      if (!isTableTag(row, 'tr')) return row;
      const newCells = React.Children.map(row.props.children, (cell, index) => {
        if (isTableTag(section, 'thead') && isTableTag(cell, 'th')) {
          return React.cloneElement(cell, { role: 'columnheader', scope: 'col' });
        }
        if (isTableTag(section, 'tbody') && isTableTag(cell, 'td')) {
          return React.cloneElement(cell, {
            role: 'cell',
            'data-label': headers[index] ?? '',
          });
        }
        return cell;
      });
      return React.cloneElement(row, { role: 'row' }, newCells);
    });
    return React.cloneElement(section, { role: 'rowgroup' }, newRows);
  });

  // Keep table semantics explicit when mobile CSS changes the native display values.
  return React.cloneElement(table, { role: 'table' }, newChildren);
};

// Helper function to convert markdown syntax to HTML
const convertMarkdownToHtml = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>');

// Component to convert markdown table text to HTML table
type MarkdownTableConverterProps = {
  content: string;
};

const MarkdownTableConverter = ({ content }: MarkdownTableConverterProps) => {
  try {
    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.log('MarkdownTableConverter - Input content:', `${content.substring(0, 200)}...`);
      /* eslint-enable no-console */
    }

    // Parse markdown table content into HTML table
    const lines = content
      .trim()
      .split('\n')
      .filter((line) => line.trim());

    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.log('MarkdownTableConverter - Lines:', lines);
      /* eslint-enable no-console */
    }

    if (lines.length < 2) {
      // Not a valid table, return original content
      if (process.env.NODE_ENV === 'development') {
        /* eslint-disable no-console */
        console.log('MarkdownTableConverter - Not enough lines, returning original');
        /* eslint-enable no-console */
      }
      return <p>{content}</p>;
    }

    // First line is header, second line is separator, rest are data rows
    const headerLine = lines[0];
    const separatorLine = lines[1];
    const dataLines = lines.slice(2);

    // Validate that this is a table (separator line should contain dashes)
    if (!separatorLine.includes('-')) {
      if (process.env.NODE_ENV === 'development') {
        /* eslint-disable no-console */
        console.log('MarkdownTableConverter - No separator found, returning original');
        /* eslint-enable no-console */
      }
      return <p>{content}</p>;
    }

    // Parse header
    const headers = headerLine
      .split('|')
      .map((cell) => cell.trim())
      .filter((cell) => cell.length > 0);

    // Parse data rows
    const rows = dataLines.map((line) =>
      line
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0)
    );

    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.log('MarkdownTableConverter - Headers:', headers);
      console.log('MarkdownTableConverter - Rows:', rows);
      /* eslint-enable no-console */
    }

    // Validate table structure
    if (headers.length === 0 || rows.some((row) => row.length !== headers.length)) {
      if (process.env.NODE_ENV === 'development') {
        /* eslint-disable no-console */
        console.log('MarkdownTableConverter - Invalid table structure, returning original');
        console.log('Headers length:', headers.length);
        console.log(
          'Rows with wrong length:',
          rows.filter((row) => row.length !== headers.length)
        );
        /* eslint-enable no-console */
      }
      return <p>{content}</p>;
    }

    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.log('MarkdownTableConverter - Creating HTML table');
      /* eslint-enable no-console */
    }

    // Explicit roles retain table semantics when mobile CSS changes display to block/flex.
    /* eslint-disable jsx-a11y/no-redundant-roles, jsx-a11y/no-interactive-element-to-noninteractive-role */
    return (
      <div className={styles['table-container']}>
        <table role="table">
          <thead role="rowgroup">
            <tr role="row">
              {headers.map((header, index) => (
                // eslint-disable-next-line react/no-danger
                <th
                  key={index}
                  role="columnheader"
                  scope="col"
                  dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(header) }}
                />
              ))}
            </tr>
          </thead>
          <tbody role="rowgroup">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} role="row">
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    role="cell"
                    data-label={headers[cellIndex] ?? ''}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(cell) }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    /* eslint-enable jsx-a11y/no-redundant-roles, jsx-a11y/no-interactive-element-to-noninteractive-role */
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.error('MarkdownTableConverter - Error:', error);
      /* eslint-enable no-console */
    }
    // Return original content on error
    return <p>{content}</p>;
  }
};

export default Base;

type TableOfContentsProps = {
  headings: Heading[];
};

const TableOfContents = ({ headings }: TableOfContentsProps) => (
  <div className={styles['table-of-contents-base']}>
    <div className={styles['title-with-icon']}>
      <ListIcon className={styles['list-icon']} />
      <h2 className={`${styles['title-wrapper']} table-of-contents`}>目次</h2>
    </div>
    <div className={styles['items-wrapper']}>
      {headings.map((heading) => (
        <div key={heading.value} className={heading.level === 2 ? styles['item-level-2'] : styles['item-level-3']}>
          <a href={`#${heading.slug}`}>{heading.value}</a>
        </div>
      ))}
    </div>
  </div>
);
